/**
 * Session API Service
 *
 * Handles portal metrics endpoints (login tracking and feedback submission).
 * Uses separate endpoint from main API Gateway.
 */

import { getIdToken } from '../auth';

// Get Session API Gateway URL from runtime configuration
const getSessionAPIURL = () => {
  const endpoint = window.amplifyConfig?.API?.sessionApi?.endpoint;
  if (!endpoint) {
    console.error('Session API Gateway endpoint not configured in window.amplifyConfig');
    return '';
  }
  return endpoint;
};

/**
 * Record user login event (fire-and-forget).
 * Silently fails on error - login tracking shouldn't block user experience.
 *
 * @param {string} userEmail - User's email address
 */
export const recordLogin = (userEmail) => {
  const apiUrl = getSessionAPIURL();
  if (!apiUrl) {
    console.warn('Session API URL not configured, skipping login tracking');
    return;
  }

  fetch(`${apiUrl}/login-event`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ user_id: userEmail }),
  })
    .then((response) => {
      if (response.ok) {
        console.log('Login event recorded successfully');
      } else {
        console.warn('Login tracking failed:', response.statusText);
      }
    })
    .catch((error) => {
      // Silent failure - don't block user on login tracking error
      console.warn('Login tracking error (non-blocking):', error);
    });
};

/**
 * Submit user feedback.
 * Throws on error so caller can display error message.
 *
 * @param {string} userEmail - User's email address
 * @param {Object} item - Feedback payload
 * @param {string} item.email - User's email
 * @param {string} item.customerName - Customer name
 * @param {string} item.type - Feedback type ('feedback' or 'defect')
 * @param {number} [item.hoursSaved] - Hours saved (required for feedback type)
 * @param {string} [item.comments] - Optional comments
 * @returns {Promise<void>}
 * @throws {Error} If submission fails
 */
export const submitFeedback = async (userEmail, item) => {
  const apiUrl = getSessionAPIURL();
  if (!apiUrl) {
    throw new Error('Session API URL not configured');
  }

  try {
    const idToken = await getIdToken();
    if (!idToken) {
      throw new Error('Authentication required to submit feedback');
    }

    const response = await fetch(`${apiUrl}/feedback`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        user_id: userEmail,
        item,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Failed to submit feedback: ${response.statusText}`);
    }

    console.log('Feedback submitted successfully');
  } catch (error) {
    console.error('Failed to submit feedback:', error);
    throw error;
  }
};
